import { sha256 } from 'js-sha256';
import { arrayBufferToBase64, base64ToArrayBuffer } from './converters';

export const getKeyPair = async () => {
  let keyPair = await crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      // Consider using a 4096-bit key for systems that require long-term security
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt']
  );

  return keyPair;
};

export const generateFeKey = async () => {
  const feKey = await crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256,
    },
    true,
    ['encrypt', 'decrypt']
  );

  return feKey;
};

export const exportFeKey = async (feKey: CryptoKey): Promise<string> => {
  const keyData = await crypto.subtle.exportKey('jwk', feKey);
  return JSON.stringify(keyData);
};

// Import the feKey from a string
export const importFeKey = async (keyString: string): Promise<CryptoKey> => {
  const keyData = JSON.parse(keyString);
  const feKey = await crypto.subtle.importKey(
    'jwk',
    keyData,
    { name: 'AES-GCM' },
    true,
    ['encrypt', 'decrypt']
  );
  return feKey;
};

export const encryptFeKeyWithPubKey = async (
  publicKey: CryptoKey,
  feKey: CryptoKey
) => {
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const exportedKey = await window.crypto.subtle.exportKey('raw', feKey);

  let encyptData = await crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
      iv,
    },

    publicKey,
    exportedKey
  );

  const encryptedDataInBytes = new Uint8Array(encyptData);

  const encryptedPackage = new Uint8Array(
    iv.length + encryptedDataInBytes.length
  );
  encryptedPackage.set(iv);
  encryptedPackage.set(encryptedDataInBytes, iv.length);

  return arrayBufferToBase64(encryptedPackage);
};

export const decryptFeKeyWithPrivateKey = async (
  privateKey: CryptoKey,
  encFeKey: string
) => {
  const convertedToArrayBuffer = base64ToArrayBuffer(encFeKey);
  const encryptedSetData = new Uint8Array(convertedToArrayBuffer);

  let iv = encryptedSetData.slice(0, 12);
  let encKey = encryptedSetData.slice(12);

  let decryptedKey = await crypto.subtle.decrypt(
    {
      name: 'RSA-OAEP',
      iv,
    },
    privateKey,
    encKey
  );

  const feKey: CryptoKey = await window.crypto.subtle.importKey(
    'raw',
    decryptedKey,
    'AES-GCM',
    true,
    ['encrypt', 'decrypt']
  );

  return feKey;
};

export const generateUserKeyWithPassKey = async (
  purpose: 'encrypt' | 'decrypt',
  passKey: string,
  salt: Uint8Array
) => {
  const encodedPass = new TextEncoder().encode(sha256(passKey));
  let passwordKey = await crypto.subtle.importKey(
    'raw',
    encodedPass,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  let aesKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 25000,
      hash: { name: 'SHA-256' },
    },
    passwordKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  return aesKey;
};

export const encryptPrivateKey = async (
  privateKey: CryptoKey,
  passKey: string
) => {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const salt = crypto.getRandomValues(new Uint8Array(32));

  let aesKey = await generateUserKeyWithPassKey('encrypt', passKey, salt);

  const exportedKey: JsonWebKey = await window.crypto.subtle.exportKey(
    'jwk',
    privateKey
  );
  const encodedData = new TextEncoder().encode(JSON.stringify(exportedKey));

  let encData = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    aesKey,
    encodedData
  );

  const encryptedDataInBytes = new Uint8Array(encData);

  const encryptedPackage = new Uint8Array(
    salt.length + iv.length + encryptedDataInBytes.length
  );
  encryptedPackage.set(salt);
  encryptedPackage.set(iv, salt.length);
  encryptedPackage.set(encryptedDataInBytes, salt.length + iv.length);
  return arrayBufferToBase64(encryptedPackage);
};

export const decryptPrivateKey = async (
  encPrivateKey: string,
  passKey: string
) => {
  let error = '';
  let privateKey: CryptoKey | undefined = undefined;

  const convertedToArrayBuffer = base64ToArrayBuffer(encPrivateKey);
  const encryptedSetData = new Uint8Array(convertedToArrayBuffer);

  const salt = encryptedSetData.slice(0, 32);
  let iv = encryptedSetData.slice(32, 44);
  let encKey = encryptedSetData.slice(44);

  let aesKey = await generateUserKeyWithPassKey('decrypt', passKey, salt);

  await crypto.subtle
    .decrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      aesKey,
      encKey
    )
    .then(async (decrypt) => {
      let originalStringifiedKey = new TextDecoder().decode(decrypt);

      const JSWKey: JsonWebKey = JSON.parse(originalStringifiedKey);

      privateKey = await window.crypto.subtle.importKey(
        'jwk',
        JSWKey,
        {
          //these are the algorithm options
          name: 'RSA-OAEP',
          hash: { name: 'SHA-256' },
        },
        true,
        ['decrypt']
      );
    })
    .catch((err) => {
      error = "The PassKey that you've entered is incorrect";
    });

  // console.log("decrypt", decrypt)

  return {
    privateKey,
    error,
  };
};
